import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/', isExternal: false },
    { label: 'Services', href: '/services', isExternal: false },
    { label: 'Departments', href: '/departments', isExternal: false },
    { label: 'Features', href: '#features', isExternal: false },
    { label: 'Blogs', href: '/blogs', isExternal: false },
    { label: 'Contact', href: '#contact', isExternal: false },
  ];

  const handleNavigation = (href, isExternal = false) => {
    if (isExternal) {
      window.open(href, '_blank');
      return;
    }

    if (href === '/') {
      // Navigate to home page
      navigate('/');
      setIsMobileMenuOpen(false);
    } else if (href === '/blogs') {
      // Navigate to blogs page
      navigate('/blogs');
      setIsMobileMenuOpen(false);
    } else if (href.startsWith('#')) {
      // Handle section scrolling
      if (location.pathname === '/') {
        // We're on home page, just scroll to section
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          setIsMobileMenuOpen(false);
        }
      } else {
        // We're on another page, navigate to home page first
        navigate('/');
        // Wait for navigation then scroll to section
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } else {
      // Regular navigation
      navigate(href);
      setIsMobileMenuOpen(false);
    }
  };

  const handleBookAppointment = () => {
    if (location.pathname === '/') {
      // Scroll to contact section on home page
      const element = document.querySelector('#contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page and then scroll to contact
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector('#contact');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[hsl(var(--card))]/95 backdrop-blur-lg shadow-[var(--shadow-md)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Adjusted vertical alignment */}
          <div 
            className="flex items-center cursor-pointer pt-1" // Added pt-1 to push logo down slightly
            onClick={() => navigate('/')}
          >
            <img 
              src="/images/logomix.png" 
              alt="NANDED MULTISPECIALITY HOSPITAL"
              className="h-17 w-auto object-contain" // Slightly reduced height to compensate for padding
              onError={(e) => {
                // Fallback if image doesn't load
                e.target.style.display = 'none';
                const fallback = document.getElementById('logo-fallback');
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            {/* Fallback logo if image doesn't load */}
            <div id="logo-fallback" className="hidden items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] flex items-center justify-center shadow-[var(--shadow-md)]">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-bold text-[hsl(var(--foreground))] leading-tight">
                  NANDED
                </span>
                <span className="text-xs text-[hsl(var(--muted-foreground))] leading-tight font-medium">
                  MULTISPECIALITY HOSPITAL
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavigation(link.href, link.isExternal)}
                className={`px-4 py-2 transition-colors duration-200 font-medium ${
                  location.pathname === link.href
                    ? 'text-[hsl(var(--primary))]'
                    : 'text-[hsl(var(--foreground))] hover:text-[hsl(var(--primary))]'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:02067527445"
              className="flex items-center space-x-2 text-[hsl(var(--primary))] hover:text-[hsl(var(--primary-dark))] transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span className="font-semibold">02067527445</span>
            </a>
            <Button
              variant="premium"
              onClick={handleBookAppointment}
              className="font-semibold"
            >
              Book Appointment
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[hsl(var(--foreground))] hover:text-[hsl(var(--primary))] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[hsl(var(--card))] border-t border-[hsl(var(--border))] shadow-[var(--shadow-lg)]">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavigation(link.href, link.isExternal)}
                className={`block w-full text-left px-4 py-3 rounded-lg transition-all duration-200 font-medium ${
                  location.pathname === link.href
                    ? 'bg-[hsl(var(--primary))] text-white'
                    : 'text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--primary))]'
                }`}
              >
                {link.label}
              </button>
            ))}
            <div className="pt-4 space-y-3">
              <a
                href="tel:02067527445"
                className="flex items-center justify-center space-x-2 text-[hsl(var(--primary))] font-semibold py-3"
              >
                <Phone className="w-5 h-5" />
                <span>02067527445</span>
              </a>
              <Button
                variant="premium"
                className="w-full font-semibold"
                onClick={handleBookAppointment}
              >
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};